import {
  createOverride,
  remoteLinkOf,
  type RecurrenceOverride,
  type RecurrenceRule,
  type Task,
} from "@app/core";
import type { LooseInstance } from "@app/core";
import { iso } from "./task-graph.js";
import type { ReconcileDeps } from "./types.js";

/**
 * Update an existing local series in place from a freshly-mapped remote copy,
 * keeping the local id / created_at / rule id and upserting its overrides by
 * RECURRENCE-ID.
 *
 * The remote link is written through `setRemoteLink`, not the content update —
 * `TasksRepository.update` no longer carries those columns (see
 * `TASK_REMOTE_LINK_FIELDS`), so an adopted etag has exactly one writer.
 *
 * Every content write here is `synced: "google"` — the content being written IS
 * Google's, so the row is atomically born google-acknowledged (and stays
 * server-dirty, so the inbound change propagates to peers). Without the
 * self-ack, applying an inbound update would bump `mut` and the same pass's
 * push would echo the row straight back at Google.
 */
export async function applyUpdate(
  deps: ReconcileDeps,
  existing: Task,
  mapped: Task,
  rule: RecurrenceRule | undefined,
  ovs: RecurrenceOverride[],
  now: Date,
  existingOverrides?: RecurrenceOverride[],
): Promise<void> {
  let recurrenceId = existing.recurrence_id;
  if (rule) {
    const localRule = recurrenceId ? await deps.recurrence.findById(recurrenceId) : null;
    if (localRule) {
      await deps.recurrence.update({
        ...localRule,
        rrule: rule.rrule,
        dtstart: rule.dtstart,
        until: rule.until,
        tz: rule.tz,
        updated_at: iso(now),
      }, "google");
    } else {
      await deps.recurrence.insert(rule, "google");
      recurrenceId = rule.id;
    }
  }
  await deps.tasks.update({
    ...mapped,
    id: existing.id,
    created_at: existing.created_at,
    recurrence_id: recurrenceId,
    subtask_parent_id: existing.subtask_parent_id,
    subtask_order: existing.subtask_order,
    // Google can't carry these — preserve the local values instead of wiping them.
    label_ids: existing.label_ids,
    topic_ids: existing.topic_ids,
  }, "google");
  await deps.tasks.setRemoteLink(existing.id, {
    ...remoteLinkOf(mapped),
    // The mapped copy is derived from the event we just read, so its identity is
    // authoritative — but never let a mapping gap erase an id we already hold.
    external_id: mapped.external_id ?? existing.external_id,
    ical_uid: mapped.ical_uid ?? existing.ical_uid,
  });
  if (recurrenceId) {
    // Index this rule's existing overrides once for all its upserts. A caller
    // updating many tasks passes `existingOverrides` to share one listAll()
    // across them (each task owns its rule, so the slices are independent).
    const pool = existingOverrides ?? (await deps.recurrenceOverrides.listAll());
    const byRef = new Map(
      pool
        .filter((x) => x.recurrence_id === recurrenceId)
        .map((x) => [x.recurrence_id_ref, x] as const),
    );
    for (const o of ovs) {
      await upsertOverride(deps, recurrenceId, { ...o, recurrence_id: recurrenceId }, byRef);
    }
  }
}

/** Insert or update an override keyed by (recurrence_id, recurrence_id_ref). Pass a
 *  pre-built `byRef` index (ref → existing override) to avoid a full `listAll()` per
 *  call when upserting several overrides for one rule; it's kept current as we go. */
export async function upsertOverride(
  deps: ReconcileDeps,
  recurrenceId: string,
  o: RecurrenceOverride,
  byRef?: Map<string, RecurrenceOverride>,
): Promise<void> {
  const index =
    byRef ??
    new Map(
      (await deps.recurrenceOverrides.listAll())
        .filter((x) => x.recurrence_id === recurrenceId)
        .map((x) => [x.recurrence_id_ref, x] as const),
    );
  const existing = index.get(o.recurrence_id_ref);
  if (existing) {
    const merged = { ...o, id: existing.id, created_at: existing.created_at };
    await deps.recurrenceOverrides.update(merged, "google");
    index.set(o.recurrence_id_ref, merged);
  } else {
    await deps.recurrenceOverrides.insert(o, "google");
    index.set(o.recurrence_id_ref, o);
  }
}

export function looseToOverride(
  recurrenceId: string,
  li: LooseInstance,
  now: Date,
): RecurrenceOverride {
  return createOverride(
    {
      recurrence_id: recurrenceId,
      recurrence_id_ref: li.recurrenceIdRef,
      range: "single",
      status: li.cancelled ? "cancelled" : "open",
      due_at: li.cancelled ? null : li.patch.due_at,
      duration_minutes: li.cancelled ? null : li.patch.duration_minutes,
      all_day: li.cancelled ? null : li.patch.all_day,
      title: li.cancelled ? null : li.patch.title,
      notes: li.cancelled ? null : li.patch.notes,
    },
    () => now,
  );
}
